import React, { ChangeEvent, FC } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Checkbox,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  makeStyles,
  Theme,
  Typography,
  useMediaQuery,
} from '@material-ui/core';
import { CheckboxCheckedIcon, CheckboxIcon, OrangeArrowIconPos } from 'svgs';

export interface FilterListItem {
  label: string;
  count: number;
  id: string;
}

export interface FilterItemProps {
  filterName: string;
  filterId: string;
  listItems: FilterListItem[];
  checks: string[];
  onChange: (filterId, checks: string[]) => void;
  careersTotal: number;
}

const useStyles = makeStyles((theme: Theme) => ({
  divider: {
    height: 2,
    maxWidth: '100%',
    backgroundColor: theme.palette.primary.contrastText,
    margin: theme.spacing(3, 0),
  },
  title: {
    fontWeight: 500,
    lineHeight: 1.3,
    paddingLeft: theme.spacing(6),
  },
  accordion: {
    boxShadow: 'none',
    backgroundColor: theme.palette.common.white,
    margin: 0,
    '&:before': {
      display: 'none',
    },
    '& .MuiAccordionSummary-root': {
      padding: 0,
      minHeight: 26,
      maxHeight: 26,
      display: 'inline-flex',
      position: 'relative',
      '& .MuiAccordionSummary-content': {
        margin: 0,
      },
      '& .MuiButtonBase-root': {
        position: 'absolute',
        top: 0,
        left: -10,
        bottom: 0,
      },
    },
    '& .MuiAccordionSummary-expandIcon': {
      color: 'transparent',
    },
  },
  accordionDetails: {
    padding: 0,
  },
  listItem: {
    padding: 0,
    '&:hover': {
      backgroundColor: 'transparent',
    },
    '&:focus': {
      color: 'transparent',
    },
    '& .MuiListItemIcon-root': {
      minWidth: theme.spacing(4),
      '& span': {
        '&:hover': {
          backgroundColor: 'transparent',
        },
      },
    },
    '& .MuiTouchRipple-root': {
      color: 'transparent',
    },
  },
  checkbox: {
    padding: theme.spacing(1.5, 2),
  },
  listItemText: {
    '& span': {
      fontSize: 15,
      color: theme.palette.secondary.dark,
    },
  },
  listItemActiveText: {
    '& span': {
      fontSize: 15,
      color: theme.palette.primary.dark,
      fontWeight: 500,
    },
  },
  text: {
    fontWeight: 500,
    padding: theme.spacing(2, 0, 1.5),
  },
}));

const FilterItem: FC<FilterItemProps> = (props) => {
  const { filterName, listItems, filterId, checks, onChange, careersTotal } = props;
  const classes = useStyles();
  const [expanded, setExpanded] = React.useState(true);
  const [mobileExpanded, setMobileExpanded] = React.useState(false);
  const [expandedLongList, setExpandedLongList] = React.useState(false);
  const isMedium = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));

  const handleChange = (event: ChangeEvent, isExpanded: boolean) => {
    setExpanded(isExpanded);
  };

  const mobileHandleChange = (event: ChangeEvent, mobileExpanded: boolean) => {
    setMobileExpanded(mobileExpanded);
  };

  const onToggleCheck = (id: string) => () => {
    const index = checks.indexOf(id);
    if (index >= 0) {
      const newChecks = [...checks];
      newChecks.splice(index, 1);
      onChange(filterId, newChecks);
    } else {
      onChange(filterId, [...checks, id]);
    }
  };

  return (
    <>
      <Divider className={classes.divider} />
      <Accordion
        expanded={isMedium ? expanded : mobileExpanded}
        onChange={isMedium ? handleChange : mobileHandleChange}
        className={classes.accordion}
      >
        <AccordionSummary
          expandIcon={expanded ? <OrangeArrowIconPos position="right" /> : <OrangeArrowIconPos position="right" />}
        >
          <Typography className={classes.title} color="textSecondary">
            {filterName}
          </Typography>
        </AccordionSummary>
        <AccordionDetails className={classes.accordionDetails}>
          <List>
            {listItems.map(({ label, count, id }, index) => {
              const labelId = `checkbox-list-label-${index}`;
              return (
                index < 5 && (
                  <ListItem
                    key={id}
                    role={undefined}
                    dense
                    button
                    onClick={() => {
                      if (count > 0 || checks.includes(id)) onToggleCheck(id);
                    }}
                    className={classes.listItem}
                  >
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={checks.includes(id)}
                        tabIndex={-1}
                        disableRipple
                        disabled={!count && !checks.includes(id)}
                        inputProps={{ 'aria-labelledby': labelId }}
                        className={classes.checkbox}
                        icon={<CheckboxIcon />}
                        checkedIcon={<CheckboxCheckedIcon />}
                        onChange={onToggleCheck(id)}
                      />
                    </ListItemIcon>
                    <ListItemText
                      id={labelId}
                      primary={`${label} (${count < careersTotal ? count : careersTotal})`}
                      className={checks.includes(id) ? classes.listItemActiveText : classes.listItemText}
                    />
                  </ListItem>
                )
              );
            })}
            {listItems.length > 5 && expandedLongList && (
              <List style={{ paddingTop: 0 }}>
                {listItems.map(({ label, count, id }, index) => {
                  const labelId = `checkbox-list-label-${index}`;
                  return (
                    index > 4 && (
                      <ListItem
                        key={index}
                        role={undefined}
                        dense
                        button
                        onClick={() => {
                          if (count > 0) onToggleCheck(id);
                        }}
                        className={classes.listItem}
                      >
                        <ListItemIcon>
                          <Checkbox
                            edge="start"
                            checked={checks.includes(id)}
                            tabIndex={-1}
                            disableRipple
                            disabled={!count}
                            inputProps={{ 'aria-labelledby': labelId }}
                            className={classes.checkbox}
                            icon={<CheckboxIcon />}
                            checkedIcon={<CheckboxCheckedIcon />}
                            onChange={onToggleCheck(id)}
                          />
                        </ListItemIcon>
                        <ListItemText
                          id={labelId}
                          primary={`${label} (${count})`}
                          className={checks.includes(id) ? classes.listItemActiveText : classes.listItemText}
                        />
                      </ListItem>
                    )
                  );
                })}
              </List>
            )}
            {listItems.length > 5 && (
              <AccordionSummary onClick={() => setExpandedLongList(!expandedLongList)}>
                {expandedLongList ? (
                  <Typography color="primary" className={classes.text}>
                    Show Less
                  </Typography>
                ) : (
                  <Typography color="primary" className={classes.text}>
                    Show More
                  </Typography>
                )}
              </AccordionSummary>
            )}
          </List>
        </AccordionDetails>
      </Accordion>
    </>
  );
};

export default FilterItem;
